"use client";
import {
  MoreHorizontal,
  Eye,
  CheckCircle,
  Ban,
  Trash2 as Delete,
  Search,
  Filter,
  CheckCheck,
  Copy,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  useAddReferralApiCallMutation,
  useAddUsersReferralApiCallMutation,
  useChangeStatusReferralApiCallMutation,
  useChangeStatusUsersReferralApiCallMutation,
  useDeleteReferralApiCallMutation,
  useDeleteReferralUsersApiCallMutation,
  useGetProfileApiCallQuery,
  useGetRefferalsApiCallQuery,
  useGetRefferalsCodeApiCallQuery,
  useGetUsersRefferalsCodeApiCallQuery,
  useLazyGetRefferalsApiCallQuery,
} from "../../services/api/auth/auth.api";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError, Refferal } from "../../services/api/auth/auth.type";
import { toast } from "sonner";
import useDebounce from "../../hooks/useDebounce";
import { Dialog, DialogContent, DialogHeader } from "../ui/dialog";
import shortenAddress from "../../utils/shortenAddress";
import { REFERRAL_BASE_URL } from "../../constants/constant";
import {
  useGetUsersApiCallQuery,
  useLazyGetUsersApiCallQuery,
} from "../../services/api/users/users.api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ScrollArea } from "../ui/scroll-area";
import { User } from "../../services/api/users/user.type";
import { useGetTokenPriceConversionApiCallQuery } from "../../services/api/campaigns/campaigns.api";

const UserReferrals = () => {
  const [domain] = useState(REFERRAL_BASE_URL);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [referralsPage, setReferralsPage] = useState(1);
  const [referralsListsPage, setReferralsListsPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);

  const [search, setSearch] = useState("");

  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [userPage, setUserPage] = useState(1);
  const [userTotalPage, setUserTotalPage] = useState(0);

  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [newReferral, setNewReferral] = useState("");
  // const [domain, setDomain] = useState(REFERRAL_BASE_URL);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [currentIndex, setcurrentIndex] = useState<number | null>(null);

  // const [referrals, setReferrals] = useState<Referral[]>(dummyReferrals);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isAddReferralLoading, setIsAddReferralLoading] = useState(false);

  const [refferalsLists, setRefferalsLists] = useState<Refferal[]>([]);

  const [selectedReferral, setSelectedReferral] = useState<Refferal | null>(
    null
  );
  const [showReferralDetails, setShowReferralDetails] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 500);

  // const {
  //   data: usersData,
  //   isLoading: isUsersLoading,
  //   isError,
  // } = useGetUsersApiCallQuery({
  //   search: debouncedSearch || "",
  //   status: statusFilter === "all" ? "" : statusFilter || "",
  //   page: page,
  //   limit: 10,
  // });

  const [getUsers] = useLazyGetUsersApiCallQuery();

  const {
    data: referralsData,
    isLoading: isReferralsLoading,
    isError: isReferralsError,
  } = useGetUsersRefferalsCodeApiCallQuery({
    search: debouncedSearch || "",
    status: statusFilter === "all" ? "" : statusFilter || "",
    page: page,
    limit: 10,
  });

  const [getReferralsList, { data: referralsDataLazy }] =
    useLazyGetRefferalsApiCallQuery();
  const [addReferral] = useAddUsersReferralApiCallMutation();
  const [changeStatusReferral] = useChangeStatusUsersReferralApiCallMutation();
  const [deleteReferral] = useDeleteReferralUsersApiCallMutation();

  const confirmDelete = async () => {
    setIsDeleteLoading(true);
    try {
      const data = await deleteReferral({
        _id: deleteTargetId as string,
      }).unwrap();
      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        setDeleteTargetId(null);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error deleting campaign");
    } finally {
      setIsDeleteLoading(false);
      setDeleteTargetId(null);
    }
  };

  const handleAddReferral = async () => {
    setIsAddReferralLoading(true);
    try {
      const response = await addReferral({
        code: newReferral,
        userId: selectedUser?._id,
      }).unwrap();

      if (response.responseCode === 200) {
        toast.success(response.responseMessage);
        setNewReferral("");
        setSelectedUser(null);
        setSearch("");
        setIsOpen(false);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Login failed");
    } finally {
      setIsAddReferralLoading(false);
    }
  };

  const handleChangeStatus = async (userId: string) => {
    try {
      const data = await changeStatusReferral({
        _id: userId,
      }).unwrap();
      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(
        errorData?.responseMessage || "Error changing campaign status"
      );
    }
  };

  const handleViewDetails = async (ref: Refferal) => {
    setShowReferralDetails(true);
    setSelectedReferral(ref);
    console.log(ref);

    try {
      const response = await getReferralsList({
        userId: ref?.userId || "",
        referrerCode: ref.code,
        page: referralsPage,
        limit: 10,
      }).unwrap();
      setRefferalsLists(response.result?.docs || []);
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      console.log(errorData);
      setRefferalsLists([]);
      // toast.error(errorData?.responseMessage || "Login failed");
    }
  };

  const handleCopyReferral = (referralLink: string, index: number) => {
    setcurrentIndex(index);
    navigator.clipboard.writeText(referralLink);
    setCopiedReferral(true);
    setTimeout(() => setcurrentIndex(null), 2000);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      let page = userPage + 1;

      if (page <= userTotalPage) {
        setUserPage(page);
        fetchUsers(page);
      }
    }
  };

  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     const domain = window.location.origin; // e.g. https://example.com
  //     setDomain(domain);
  //   }
  // }, []);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);

    if (e.target.value === "") {
      setUsers(allUsers);
      return;
    }

    const filterSearch = allUsers?.filter((user) => {
      return user?.walletAddress
        ?.toLowerCase()
        .includes(e.target.value.toLowerCase());
    });

    setUsers(() => {
      return [...filterSearch];
    });
  };

  const fetchUsers = async (page: number) => {
    try {
      const response = await getUsers({
        search: debouncedSearch || "",
        status: statusFilter === "all" ? "" : statusFilter || "",
        page: page,
        limit: 10,
      }).unwrap();

      if (response.responseCode === 200) {
        if (response.result) {
          setUserTotalPage(response.result.pages);
          setUsers((prev) => [...prev, ...(response.result?.docs || [])]);
          setAllUsers((prev) => [...prev, ...(response.result?.docs || [])]);
        }
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (isOpen) {
      fetchUsers(1);
    }
  }, [isOpen]);

  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };
  return (
    <section>
      <h2 className="text-xl font-semibold mb-6">Users Referrals Management</h2>
      <Card>
        <CardHeader>
          <div className="flex justify-end gap-2 items-center">
            {/* <Input
              placeholder="Enter referral code"
              value={newReferral}
              onChange={(e) => setNewReferral(e.target.value)}
              className="w-48 border-gray-300 max-w-96 w-full"
            /> */}
            <Button
              //   disabled={!newReferral || isAddReferralLoading}
              onClick={() => setIsOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Add{" "}
              {/* {isAddReferralLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )} */}
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by referral code"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="BLOCK">Block</SelectItem>
                </SelectContent>
              </Select> */}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Referral Code</TableHead>
                  <TableHead>Wallet Address</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Total Amount USDT</TableHead>

                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isReferralsError ||
                referralsData?.result?.docs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No user's referrals found
                    </TableCell>
                  </TableRow>
                ) : isReferralsLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : (
                  referralsData?.result &&
                  referralsData?.result?.docs?.map((ref, index) => (
                    <TableRow key={ref._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm">{`${domain}/ref/${ref.code}`}</span>

                          <Button
                            size="sm"
                            onClick={() =>
                              handleCopyReferral(
                                `${domain}/ref/${ref.code}`,
                                index
                              )
                            }
                            className={`cursor-pointer text-xs  flex items-center gap-1 ${
                              currentIndex === index
                                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                : "bg-primary hover:bg-primary/90 text-primary-foreground"
                            }`}
                          >
                            <Copy size={8} />{" "}
                            {index === currentIndex && <div>Copied</div>}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        {shortenAddress(ref?.user?.walletAddress)}
                      </TableCell>
                      <TableCell>{ref?.amount}</TableCell>
                      <TableCell>
                        $
                        {ref.amount ? getUSDTBalance(ref.amount).toFixed(5) : 0}{" "}
                      </TableCell>

                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            ref.status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-700"
                          }`}
                        >
                          {ref.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(ref.createdAt).toLocaleDateString()}
                      </TableCell>

                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel>Actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                handleViewDetails(ref);
                                // handleViewTransactions(user);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {ref.status.toLowerCase() !== "active" ? (
                              <DropdownMenuItem
                                onClick={() => handleChangeStatus(ref._id)}
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Active
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => handleChangeStatus(ref._id)}
                                className="text-red-600"
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                Block
                              </DropdownMenuItem>
                            )}

                            <DropdownMenuItem
                              onClick={() => setDeleteTargetId(ref._id)}
                              className="text-red-600 cursor-pointer"
                            >
                              <Delete className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* popup for delete confirmation  */}
            {deleteTargetId && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl max-w-sm w-full">
                  <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                    Confirm Delete
                  </h2>
                  <p className="mb-6 text-gray-600 dark:text-gray-300">
                    Are you sure you want to delete this campaign?
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => setDeleteTargetId(null)}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
                      onClick={confirmDelete}
                    >
                      Yes, Delete{" "}
                      {isDeleteLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {referralsData?.result && referralsData?.result?.pages > 1 && (
            <div className="flex justify-end items-center mt-4 gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage((prev) => prev - 1)}
              >
                Previous
              </Button>
              <span>
                Page {page} of {referralsData.result?.pages}
              </span>
              <Button
                variant="outline"
                disabled={page === referralsData.result.pages}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
        <DialogContent className="w-full mx-auto h-[360px] flex flex-col">
          <DialogHeader>
            {/* <DialogTitle>Referral Details</DialogTitle> */}
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Enter referral code"
                value={newReferral}
                onChange={(e) => setNewReferral(e.target.value)}
                className="border-gray-300 max-w-96 w-full"
              />
              <Button
                disabled={!newReferral || !selectedUser?.walletAddress}
                onClick={handleAddReferral}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Add{" "}
                {isAddReferralLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
              </Button>
            </div>

            <div>
              <h2 className="font-medium mb-2">Users</h2>
              {/* Toggle open */}
              <Button
                variant="outline"
                className="w-full justify-between border-2 border-gray-500"
                onClick={() => setOpenDropdown((prev) => !prev)}
              >
                {selectedUser
                  ? `${selectedUser?.walletAddress}`
                  : "Select User"}
              </Button>

              {openDropdown && (
                <>
                  <Input
                    placeholder="Search users..."
                    value={search}
                    onChange={handleSearch}
                    className="focused:ring-0 border-gray-300"
                  />
                  <div
                    className="mt-1 h-48 w-full border rounded-md overflow-y-auto bg-white shadow"
                    onScroll={handleScroll}
                  >
                    <div className="divide-y">
                      {users?.length > 0 ? (
                        users?.map((user, index) => (
                          <div
                            key={user._id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setSelectedUser(user);
                              setOpenDropdown(false);
                            }}
                          >
                            <p className="text-sm font-medium">
                              {user?.walletAddress}
                            </p>
                          </div>
                        ))
                      ) : (
                        <div>
                          <p className="text-center p-2 text-sm text-gray-500">
                            No users found
                          </p>
                        </div>
                      )}
                    </div>

                    {loading && (
                      <p className="text-center p-2 text-sm text-gray-500">
                        Loading...
                      </p>
                    )}

                    {!hasMore && !loading && (
                      <p className="text-center p-2 text-sm text-gray-400">
                        No more users
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showReferralDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          {/* Popup container */}
          <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl max-h-[90vh] h-full overflow-y-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between border-b pb-3 mb-4">
              <h2 className="text-xl font-semibold">Referrals Lists</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setShowReferralDetails(false)}
              >
                ✕
              </button>
            </div>

            {/* Your Table component */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Wallet Address</TableHead>
                  <TableHead>Total Campaigns</TableHead>
                  <TableHead>Total Claimed</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {refferalsLists?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      No referrals found
                    </TableCell>
                  </TableRow>
                ) : (
                  refferalsLists?.map((ref, index) => (
                    <TableRow key={ref._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="flex">{ref?.walletAddress}

                         {ref && ref?.walletAddress ? (
                                                    <Copy
                                                      className=" h-4 cursor-pointer text-gray-500 hover:text-black ml-2"
                                                      onClick={() => {
                                                        navigator.clipboard.writeText(
                                                          ref.walletAddress
                                                        );
                                                        toast.success("Wallet address copied!");
                                                      }}
                                                    />
                                                  ) : (
                                                    <span>--</span>
                                                  )}{" "}
                      </TableCell>
                      <TableCell>{ref?.totalCampaignsClaimed}</TableCell>
                      <TableCell>{ref?.totalClaimed}</TableCell>
                      <TableCell>
                        {new Date(ref.createdAt).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {referralsDataLazy?.result &&
              referralsDataLazy?.result?.pages > 1 && (
                <div className="flex justify-end items-center mt-4 gap-2">
                  <Button
                    variant="outline"
                    disabled={referralsListsPage === 1}
                    onClick={() => setPage((prev) => prev - 1)}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {referralsListsPage} of{" "}
                    {referralsDataLazy.result?.pages}
                  </span>
                  <Button
                    variant="outline"
                    disabled={
                      referralsListsPage === referralsDataLazy.result.pages
                    }
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
          </div>
        </div>
      )}
    </section>
  );
};

export default UserReferrals;
